<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, Teleport, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useClipboard } from "@vueuse/core"
import { ColumnConfig } from "@types"

type Props = {
    /**
     * Nom de l'dentifiant unique
     */
    dataKey: string
    /**
     * Liste des colonnes à afficher
     */
    columns: ColumnConfig[]
    /**
     * Liste des valeurs à afficher dans le tableau
     */
    value: any[]
    /**
     * Liste des lignes sélectionnées
     */
    selection?: any[]
    /**
     * Indique si les lignes sont sélectionnables
     */
    selectable?: boolean
    /**
     * Indique si une pagination doit être affichée
     */
    paginator?: boolean
    /**
     * Nombre de lignes par page
     */
    rows?: number
    /**
     * Liste des options de nombre de lignes par page
     */
    rowsPerPageOptions?: number[]
    /**
     * Indique si le tableau est scrollable
     */
    scrollable?: boolean
    /**
     * Indique si une barre de recherche doit être affichée
     */
    search?: boolean
    /**
     * Identifiant du DOM où afficher la barre de recherche
     */
    teleportSearch?: string
    /**
     * Largeurs des lignes
     */
    size?: string
    /**
     * Indique si le paginator est petit
     */
    smallPaginator?: boolean
    /**
     * Mode de sélection : "single" (un seul nœud, cf. PrimeVue TreeTable) ou "checkbox" (multiple avec cases à cocher).
     * Ignoré si selectable est false.
     */
    selectionMode?: "single" | "checkbox"
    /**
     * Affiche le masque de chargement sur le tableau.
     */
    loading?: boolean
    /**
     * Message affiché lorsqu'il n'y a aucune donnée. Par défaut : "Aucune donnée".
     */
    emptyMessage?: string
}

// Définition des props
const props = withDefaults(defineProps<Props>(), {
    selection: () => [],
    selectable: false,
    paginator: false,
    rows: 10,
    rowsPerPageOptions: () => [5, 10, 15],
    scrollable: false,
    search: false,
    teleportSearch: "",
    size: "md",
    smallPaginator: false,
    selectionMode: undefined,
    loading: false,
    emptyMessage: undefined,
})

const expandedKeys = defineModel<Record<string, boolean>>("expandedKeys", {
    default: () => ({}),
})

// Définition des événements émis par le composant
const emit = defineEmits([
    "update:selection",
    "click:popup",
    "click:cell",
    "click:selectAll",
])

const { t }: { t: (key: string) => string } = useI18n({
    messages: {
        fr: {
            search: "Rechercher dans la liste",
        },
    },
})

// ============================================
// Gestion du tableau
// ============================================
/** Mode de sélection effectif : prop ou défaut checkbox quand selectable. */
const getEffectiveSelectionMode = computed(() => {
    if (!props.selectable) return "none"
    return props.selectionMode ?? "checkbox"
})

const getPropsForDataTable = computed(() => {
    const propsDataTable = {
        selectable: props.selectable,
        paginator: props.paginator,
        rows: props.rows,
        rowsPerPageOptions: props.rowsPerPageOptions,
        scrollable: props.scrollable,
        dataKey: props.dataKey,
        selectionMode: getEffectiveSelectionMode.value,
        resizableColumns: false,
        size: props.size,
        loading: props.loading,
    } as Record<string, any>

    if (props.scrollable) {
        propsDataTable["scrollHeight"] = "flex"
    }

    if (props.search) {
        propsDataTable["globalFilterFields"] = props.columns
            /**
             * On ne prend que les champs qui sont affichés dans le tableau
             */
            .map((column) => {
                // Si la colonne représente des chips, on filtre en concaténant les valeurs des chips
                if (column.chip) {
                    return `${column.field}__chips`
                }

                return column.field
            })
        propsDataTable["pt"] = {
            header: "base-row-table-header",
        }
    }

    return propsDataTable
})

// Trouve tous les élements dans le tree "value" qui sont cochés
// de façon récursive

function findNodeByKeyInTree(tree: any[], key: string) {
    for (const node of tree) {
        if (node[props.dataKey] === key) {
            return node
        }

        if (node.children) {
            const found: any = findNodeByKeyInTree(node.children, key)
            if (found) {
                return found
            }
        }
    }
    return null
}

/**
 * Depuis un noeud, trouve tous les parents dans le tree en remontant l'arbre et
 * en les ajoutant dans un tableau
 * @param key
 */
function findAllParentsInTreeFromANode(tree: any[], key: string) {
    const parents: any[] = []
    function traverse(node: any, ancestors: any[]) {
        if (node[props.dataKey] === key) {
            parents.push(...ancestors)
            return true
        }

        if (node.children) {
            for (const child of node.children) {
                if (traverse(child, [...ancestors, node])) {
                    return true
                }
            }
        }

        return false
    }

    for (const node of tree) {
        if (traverse(node, [])) {
            break
        }
    }

    return parents
}

const selectedRows = ref<
    Record<
        string,
        {
            partialChecked: boolean
            checked: boolean
        }
    >
>({})

/** En mode single : clés sélectionnées (format PrimeVue : { [key]: true }). */
const selectedKeysSingle = ref<Record<string, boolean>>({})

/** Binding pour BaseTreeTable : format selon selectionMode (single = { [key]: true }, checkbox = { [key]: { checked, partialChecked } }). */
const getSelectionKeysForTable = computed({
    get() {
        return getEffectiveSelectionMode.value === "single"
            ? selectedKeysSingle.value
            : selectedRows.value
    },
    set(val: Record<string, unknown>) {
        if (getEffectiveSelectionMode.value === "single") {
            selectedKeysSingle.value =
                val && typeof val === "object"
                    ? (val as Record<string, boolean>)
                    : {}
        } else {
            selectedRows.value =
                (val as Record<
                    string,
                    { partialChecked: boolean; checked: boolean }
                >) ?? {}
        }
    },
})

const getSelectedRowsList = computed(() => {
    if (getEffectiveSelectionMode.value === "single") {
        const key = Object.keys(selectedKeysSingle.value).find(
            (k) => selectedKeysSingle.value[k] === true
        )
        const node = key ? findNodeByKeyInTree(props.value, key) : null
        return node ? [node] : []
    }
    return (
        Object.entries(selectedRows.value)
            .map(([key, value]) => {
                if (value?.partialChecked) return null
                const node = findNodeByKeyInTree(props.value, key)
                return node
            })
            ?.filter((node) => node) ?? []
    )
})

watch(
    getSelectedRowsList,
    (newValue, oldValue) => {
        const keyOldValue = (oldValue ?? [])?.map((node) => node.key)
        const keyNewValue = newValue.map((node) => node.key)

        const diff = keyNewValue.filter((key) => !keyOldValue.includes(key))

        if (diff.length > 0 || keyOldValue.length !== keyNewValue.length) {
            emit("update:selection", newValue)
        }
    },
    { deep: true, immediate: true }
)

const getAllRows = computed(() => {
    // Récupère toutes les lignes de façon récursive en se basant sur "children"
    function getAllChildren(node: any) {
        const children = node.children || []
        return [
            node,
            ...children.flatMap((child: any) => getAllChildren(child)),
        ]
    }
    return props.value.flatMap((node) => getAllChildren(node))
})
/**
 * Indique l'état de la checkbox de sélection totale
 */
const isSelectAll = ref(false)
/**
 * Quand l'utilisateur clique sur la checkbox de sélection totale
 */
function onSelectAll() {
    isSelectAll.value = !isSelectAll.value

    if (!isSelectAll.value) {
        selectedRows.value = {}
        return
    }

    selectedRows.value = getAllRows.value.reduce(
        (acc, node) => {
            acc[node.key] = {
                checked: isSelectAll.value,
                partialChecked: false,
            }
            return acc
        },
        {} as Record<string, { checked: boolean; partialChecked: boolean }>
    )
}

watch(
    () => props.selection,
    (newValue) => {
        if (!newValue || !Array.isArray(newValue)) {
            if (getEffectiveSelectionMode.value === "single") {
                selectedKeysSingle.value = {}
            } else {
                selectedRows.value = {}
            }
            return
        }
        if (getEffectiveSelectionMode.value === "single") {
            selectedKeysSingle.value =
                newValue.length > 0 ? { [newValue[0].key]: true } : {}
            return
        }
        selectedRows.value = newValue.reduce(
            (acc, node) => {
                acc[node.key] = {
                    checked: true,
                    partialChecked: false,
                }

                const parents = findAllParentsInTreeFromANode(
                    props.value,
                    node.key
                )

                for (const parent of parents) {
                    if (acc[parent.key]) continue
                    acc[parent.key] = {
                        checked: false,
                        partialChecked: true,
                    }
                }

                return acc
            },
            {} as Record<string, { checked: boolean; partialChecked: boolean }>
        )
        isSelectAll.value =
            getAllRows.value.length === getSelectedRowsList.value.length
    },
    { immediate: true }
)

// ============================================
// Gestion des filtres
// ============================================
/**
 * Liste des filtres sur le tableau
 */
const filters = ref<{ global: null | string }>({ global: "" })

// Mise à jour quand TreeTable nous signale un changement de sélection
function onSelectionChange(e: any) {
    // selection.value = e.value
    emit("update:selection", e.value)
}

// =====================================================
// Gestion du overflow du text pour afficher le tooltip
// =====================================================

/**
 * Liste des célules du tableau
 */
const cells = ref<HTMLElement[]>([])
/**
 * Liste des états d'overflow des cellules
 */
const ellipsedCells = ref<{ value: boolean; id: string }[]>([])

/**
 * Calcule si le texte d'une cellule est en overflow
 */
async function checkEllipsis() {
    await nextTick()
    ellipsedCells.value = cells.value
        .map((cell: any) => {
            let isOverflowing = false

            const label = cell.querySelector(".base-table-link-label")

            if (!label) return { id: cell.id, value: false }

            isOverflowing = label.scrollWidth > label.clientWidth

            return {
                id: cell.id,
                value: isOverflowing,
            }
        })
        .filter(({ value }) => value)
}

/**
 * Indique si la cellule est en overflow et doit afficher un tooltip avec le texte complet
 * @param id - Identifiant de la cellule
 * @param value - Valeur de la cellule
 */
function isCellNeedEllipsedTooltip(id: string, value: string) {
    return ellipsedCells.value.find(({ id: _id }) => _id === id)?.value
        ? {
              value,
          }
        : null
}

/**
 * Calcule si le texte d'une cellule est en overflow
 */
onMounted(() => {
    checkEllipsis()
})

function onChangePage($event: any, callback: any, callbackParent: any) {
    callback($event)
    callbackParent($event)
}

// =====================================================
// Gestion du click sur les cellules
// =====================================================
const { copy } = useClipboard()
// référence vers le menu contextuel
const menu = ref<any>(null)
// information de la cellule sélectionnée pour le menu contextuel
const selectedCell = ref()
/**
 * Récupère le menu contextuel
 */
const getContextMenu = computed(() => {
    return [
        {
            label: "Ouvrir dans un autre onglet",
            icon: "pi pi-external-link",
            command: () => {
                onClickCell(new MouseEvent("click"), {
                    data: selectedCell.value.data,
                    field: selectedCell.value.field,
                    rowIndex: selectedCell.value.index,
                    _blank: true,
                })
            },
        },
        {
            label: "Copier",
            icon: "pi pi-copy",
            command: () => {
                copy(selectedCell.value.data?.[selectedCell.value.field])
            },
        },
    ]
})

/**
 * Quand le menu contextuel doit s'ouvrir
 * @param event - Event de clique
 * @param data - Donnée de la cellule
 * @param field - Nom du champ
 * @param index - Index de la ligne
 */
function onClickOpenMenu(
    event: any,
    {
        data,
        field,
        index,
    }: {
        data: any
        field: string
        index: number
    }
) {
    // On stocke les informations de la cellule sélectionnée
    selectedCell.value = {
        data,
        field,
        index,
    }
    if (menu.value) {
        menu.value.show(event)
    }
}

/**
 * Quand l'utilisateur clique sur une cellule.
 * Si dans l'event, la touche ctrl ou cmd est appuyée, on ouvre le lien dans un nouvel onglet.
 * @param event - Event de clique
 * @param data - Donnée de la cellule
 * @param field - Nom du champ
 * @param rowIndex - Index de la ligne
 * @param _blank - Ouvrir dans un nouvel onglet
 */
function onClickCell(
    event: MouseEvent,
    {
        data,
        field,
        rowIndex,
        _blank,
    }: {
        data: any
        field: string
        rowIndex: number
        _blank?: boolean
    }
) {
    let blank = _blank
    if (event.ctrlKey || event.metaKey) {
        blank = true
    }
    emit("click:cell", {
        data,
        field,
        rowIndex,
        _blank: blank,
    })
}

defineExpose({
    showTooltip: (index: number, field: string, value: string) => {
        return isCellNeedEllipsedTooltip(`link-${field}-${index}`, value)
    },
})

// ============================================
// Gestion de la popup
// ============================================
const selectedRow = ref()

/**
 * Référence vers la popup affichant un sous tableau
 */
const popupDataTable = ref<
    | (HTMLElement & {
          show: (event?: any) => void
          hide: () => void
      })
    | null
>(null)

/**
 * Quand on clique sur un sous lien pour afficher une popup
 * @param event - Event de clique du bouton
 * @param data - Donnée de la ligne
 */
function onClickPopup(event: any, data: any, field: string, rowIndex: number) {
    if (!popupDataTable.value) return

    popupDataTable.value.hide()

    selectedRow.value = {
        ...data,
    }

    nextTick(() => {
        if (popupDataTable.value) popupDataTable.value.show(event)
        emit("click:popup", {
            event,
            data,
            field,
            rowIndex,
        })
    })
}
</script>

<template>
    <div class="base-tree-table">
        <BaseTreeTable
            :value="value"
            v-model:selection-keys="getSelectionKeysForTable"
            v-model:expanded-keys="expandedKeys"
            :filters="filters"
            filter-mode="lenient"
            v-bind="getPropsForDataTable"
            :pt="{
                header: 'base-tree-table-header bg-white border-0 p-0!',
                bodyrow: 'base-tree-table-row h-12!',
                thead: 'h-16!',
            }"
            @selection-change="onSelectionChange"
            @page="checkEllipsis"
            @update:sort-order="checkEllipsis"
        >
            <template #empty>
                <p class="p-8 text-center text-primary-700">
                    {{ props.emptyMessage ?? "Aucune donnée" }}
                </p>
            </template>
            <!-- Barre de recherche -->
            <template #header v-if="search">
                <component
                    :is="props.teleportSearch ? Teleport : 'div'"
                    defer
                    :to="teleportSearch"
                >
                    <BaseInputWrapper
                        :label="t('search')"
                        input-id="search"
                        class="base-tree-table__search max-w-[37.5rem] pb-6 mx-auto mt-3"
                    >
                        <BaseInputText
                            v-model="filters['global']"
                            input-id="search"
                        />
                    </BaseInputWrapper>
                </component>
            </template>
            <BaseColumn
                v-for="(column, index) in columns"
                :key="index"
                :field="column.field"
                :header="column.header"
                :expander="column.expander"
                :sortable="column.sortable"
                :sort-field="column.sortField ? column.sortField : column.field"
                :style="column.style"
            >
                <template
                    #header
                    v-if="
                        index === 0 && getEffectiveSelectionMode === 'checkbox'
                    "
                >
                    <BaseCheckbox
                        style="margin-left: 36px"
                        v-model="isSelectAll"
                        binary
                        @click.prevent.stop="onSelectAll"
                    />
                </template>
                <template #body="slotProps">
                    <div
                        class="base-tree-table__cell flex items-center overflow-hidden"
                        :style="column.style"
                        ref="cells"
                        :id="`link-${column.field}-${slotProps.node.key}`"
                    >
                        <div
                            v-if="
                                column.chip && !$slots[`cell_${column.field}`]
                            "
                            class="base-tree-table__cell-chips flex gap-1 whitespace-nowrap max-w-[5rem] overflow-hidden text-ellipsis"
                            :style="column.style"
                        >
                            <BaseChip
                                v-for="(tag, indexTag) in slotProps.node.data[
                                    column.field
                                ]"
                                :key="indexTag"
                                :label="tag"
                            />
                        </div>
                        <BaseButton
                            v-else-if="
                                column.link && !$slots[`cell_${column.field}`]
                            "
                            :label="slotProps.node.data[column.field]"
                            variant="link"
                            class="base-tree-table__link p-0 text-[var(--p-datatable-row-color)]! hover:text-[var(--p-datatable-row-color)]!"
                            :pt="{
                                label: 'base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis font-normal!',
                            }"
                            v-tooltip.top="
                                isCellNeedEllipsedTooltip(
                                    `link-${column.field}-${slotProps.node.key}`,
                                    slotProps.node.data[column.field]
                                )
                            "
                            @contextmenu.prevent="
                                onClickOpenMenu($event, {
                                    data: slotProps.node.data,
                                    field: column.field,
                                    index: slotProps.node.key,
                                })
                            "
                            @click="
                                onClickCell($event, {
                                    data: slotProps.node.data,
                                    field: column.field,
                                    rowIndex: slotProps.node.key,
                                })
                            "
                        />
                        <p
                            v-else-if="!$slots[`cell_${column.field}`]"
                            class="pr-4 base-row-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
                            v-tooltip.top="
                                isCellNeedEllipsedTooltip(
                                    `link-${column.field}-${slotProps.node.key}`,
                                    slotProps.node.data[column.field]
                                )
                            "
                        >
                            {{ slotProps.node.data[column.field] }}
                        </p>
                        <slot
                            v-else
                            :name="`cell_${column.field}`"
                            v-bind="{
                                data: slotProps.node.data,
                                column,
                                index: slotProps.node.key,
                            }"
                        ></slot>
                        <BaseButton
                            v-if="
                                typeof column.subLink === 'function'
                                    ? column.subLink(slotProps.node.data)
                                    : column.subLink
                            "
                            size="small"
                            variant="link"
                            label="voir"
                            severity="secondary"
                            @click="
                                onClickPopup
                                    ? onClickPopup(
                                          $event,
                                          slotProps.node.data,
                                          column.field,
                                          slotProps.node.key
                                      )
                                    : null
                            "
                        />
                    </div>
                </template>
                <!-- Slot paginatorcontainer pour intégrer le paginator personnalisé plus petit -->
                <template
                    #paginatorcontainer="propsPaginator"
                    v-if="smallPaginator"
                >
                    <BasePaginator
                        v-bind="propsPaginator"
                        :page="propsPaginator.page"
                    >
                        <template
                            #container="{
                                first,
                                last,
                                page,
                                pageCount,
                                prevPageCallback,
                                nextPageCallback,
                                totalRecords,
                            }"
                        >
                            <div
                                class="flex items-center gap-4 border border-primary bg-transparent rounded-full w-full py-1 px-2 justify-between"
                            >
                                <BaseButton
                                    icon="pi pi-chevron-left"
                                    rounded
                                    text
                                    size="small"
                                    @click="
                                        onChangePage(
                                            $event,
                                            prevPageCallback,
                                            propsPaginator.prevPageCallback
                                        )
                                    "
                                    :disabled="page === 0"
                                />
                                <div
                                    class="text-color font-medium whitespace-nowrap"
                                >
                                    <span class="hidden sm:block"
                                        >De {{ first }} à {{ last }} sur
                                        {{ totalRecords }}</span
                                    >
                                    <span class="block sm:hidden"
                                        >Page {{ page + 1 }} sur
                                        {{ pageCount }}</span
                                    >
                                </div>
                                <BaseButton
                                    icon="pi pi-chevron-right"
                                    rounded
                                    text
                                    size="small"
                                    @click="
                                        onChangePage(
                                            $event,
                                            nextPageCallback,
                                            propsPaginator.nextPageCallback
                                        )
                                    "
                                    :disabled="page === pageCount - 1"
                                />
                            </div>
                        </template>
                    </BasePaginator>
                </template>
            </BaseColumn>
        </BaseTreeTable>
        <BasePopover ref="popupDataTable">
            <slot name="popup"></slot>
        </BasePopover>
        <BaseContextMenu ref="menu" :model="getContextMenu" />
    </div>
</template>
